// 엔진 상태 패널 — /admin/health 실데이터 + 24h 통계(/admin/overview)
import { useEffect, useState } from 'react';
import { adminGet } from '../lib/adminApi';

interface HealthData {
  status: string;
  components: Record<string, { status: string; latencyMs?: number; detail?: string; version?: string }>;
}

interface OverviewStats {
  stats: { total: number; safe: number; quarantined: number; blocked: number };
  queues: { scan: Record<string, number>; webhook: Record<string, number> };
}

const ENGINE_META: Record<string, string> = {
  clamav: 'ClamAV',
  yara: 'YARA',
  redis: 'Redis Queue',
  postgres: 'PostgreSQL',
};

export function Engines() {
  const [health, setHealth] = useState<HealthData | null>(null);
  const [stats, setStats] = useState<OverviewStats | null>(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = () =>
      Promise.all([
        adminGet<HealthData>('/admin/health'),
        adminGet<OverviewStats>('/admin/overview'),
      ])
        .then(([h, s]) => { if (alive) { setHealth(h); setStats(s); setErr(false); } })
        .catch(() => { if (alive) setErr(true); });
    load();
    const id = setInterval(load, 5000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const components = health?.components ?? {
    clamav: { status: 'unknown' },
    yara: { status: 'unknown' },
    redis: { status: 'unknown' },
    postgres: { status: 'unknown' },
  };

  const displayStats = stats?.stats ?? { total: 0, safe: 0, quarantined: 0, blocked: 0 };
  const scanQ = stats?.queues?.scan ?? {};
  const totalQ = Object.values(scanQ).reduce((a, b) => a + b, 0);

  return (
    <>
      <div className="sect-head">
        <div className="sect-title">
          ⚡ 엔진 상태 <em>검사 엔진 운영 현황</em>
        </div>
        {err && (
          <span className="text-mono text-muted" style={{ fontSize: 12, color: 'var(--yellow)' }}>⚠ 연결 실패 — 마지막 값 표시</span>
        )}
      </div>

      <div className="engines-grid">
        {Object.entries(components).map(([key, c]) => {
          const isUp = c.status === 'up';
          const name = ENGINE_META[key] ?? key.toUpperCase();
          return (
            <div className="engine-card" key={key}>
              <div className="engine-name">
                <span
                  className="dot"
                  style={isUp ? undefined : { background: 'var(--text3)', boxShadow: 'none', animation: 'none' }}
                />
                {name}
              </div>
              <div className={`engine-status${isUp ? '' : ' off'}`}>
                {isUp ? 'ACTIVE' : c.status.toUpperCase()}
              </div>
              <div className="engine-meta">
                {c.latencyMs !== undefined ? `응답: ${c.latencyMs}ms` : ''}
                {c.detail ? `\n${c.detail}` : ''}
              </div>
            </div>
          );
        })}
      </div>

      <div className="card">
        <div className="card-head"><div className="card-head-title">📊 검사 통계 (누적)</div></div>
        <div style={{ padding: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 20 }}>
            <div>
              <div className="text-mono text-muted" style={{ marginBottom: 8 }}>누적 처리량</div>
              <div style={{ fontSize: 13, lineHeight: 2 }}>
                <div>총 검사: <strong>{displayStats.total}건</strong></div>
                <div>안전 판정: <strong style={{ color: 'var(--accent)' }}>{displayStats.safe}건</strong></div>
                <div>격리 처리: <strong style={{ color: 'var(--red)' }}>{displayStats.quarantined}건</strong></div>
              </div>
            </div>
            <div>
              <div className="text-mono text-muted" style={{ marginBottom: 8 }}>큐 현황</div>
              <div style={{ fontSize: 13, lineHeight: 2 }}>
                <div>scan 큐: <strong>{totalQ}건 대기</strong></div>
                {Object.entries(scanQ).map(([k, v]) => (
                  <div key={k} style={{ fontSize: 12, color: 'var(--text3)' }}>{k}: {v}</div>
                ))}
              </div>
            </div>
            <div>
              <div className="text-mono text-muted" style={{ marginBottom: 8 }}>차단 내역</div>
              <div style={{ fontSize: 13, lineHeight: 2 }}>
                <div>정책 차단: <strong style={{ color: 'var(--yellow)' }}>{displayStats.blocked}건</strong></div>
                <div>통과율: <strong>{displayStats.total > 0 ? ((displayStats.safe / displayStats.total) * 100).toFixed(1) : '0.0'}%</strong></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
